import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScanLine, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { fmtTime, todayCL } from "../lib/time";
import { StatusBadge, ReleaseBadge } from "../components/Badges";
import { CloudOff, Trash2 } from "lucide-react";
import { getLocalBins, getBundle, discardBin } from "../lib/offline";
import { useSyncState } from "../components/SyncBadge";

const PendingSync = ({ lang }) => {
  const { t, te } = useLang();
  const s = useSyncState();
  const [bins, setBins] = useState([]);
  useEffect(() => { getLocalBins().then((m) => setBins(Object.values(m))); }, [s.pending, s.conflicts.length]);
  if (!bins.length) return null;
  const errOf = (id) => s.conflicts.find((c) => c.bin_id === id)?.error;
  return (
    <section className="card border-2 border-amber-400" data-testid="pending-sync-section">
      <h2 className="px-4 pt-4 font-bold text-lg flex items-center gap-2"><CloudOff size={20} />{t("pending_sync")} ({bins.length})</h2>
      {bins.map((b) => (
        <div key={b.id} className="flex flex-col gap-1 px-4 py-2 border-b last:border-0">
          <BinRow b={b} lang={lang} />
          {errOf(b.id) && <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-bold text-red-800 px-4 pb-2" data-testid={`sync-conflict-${b.label_number}`}>
            {te(errOf(b.id))}
            <button className="btn-secondary btn-sm" onClick={() => window.confirm(t("discard_confirm")) && discardBin(b.id)} data-testid={`discard-${b.label_number}`}><Trash2 size={14} />{t("discard")}</button></div>}
        </div>
      ))}
    </section>
  );
};

export const BinRow = ({ b, lang }) => (
  <Link to={`/bin/${b.id}`} data-testid={`bin-row-${b.label_number}`}
    className="flex flex-1 items-center gap-3 p-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 min-h-[64px]">
    <span className="font-mono font-bold text-base whitespace-nowrap">{b.label_number}</span>
    <span className="text-xs text-slate-500">{fmtTime(b.activated_at, lang)}</span>
    <span className="ml-auto"><StatusBadge status={b.status} /></span>
    <ChevronRight size={18} className="text-slate-400" />
  </Link>
);

export default function SupervisorHome() {
  const { t, lang } = useLang();
  const [bins, setBins] = useState([]);
  const [asg, setAsg] = useState(null);
  const sync = useSyncState();
  useEffect(() => {
    api.get("/bins", { params: { date: todayCL(), mine: true } }).then((r) => setBins(r.data)).catch(() => {});
    api.get("/me/assignments").then((r) => setAsg(r.data)).catch(() => getBundle().then((b) => b && setAsg(b)));
  }, [sync.pending]);

  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="supervisor-home">
      <Link to="/scan" className="btn-scan w-full !min-h-[96px] text-2xl" data-testid="home-scan-button"><ScanLine size={36} />{t("scan_label")}</Link>
      <PendingSync lang={lang} />
      <section className="card">
        <h2 className="px-4 pt-4 font-bold text-lg">{t("my_bins")} <span className="text-slate-500 font-mono">({bins.length})</span></h2>
        <div data-testid="my-bins-list">
          {bins.length ? bins.map((b) => <BinRow key={b.id} b={b} lang={lang} />) : <p className="p-4 text-slate-500">{t("none")}</p>}
        </div>
      </section>
      <section className="card p-4" data-testid="my-assignments">
        <h2 className="font-bold text-lg mb-3">{t("my_assignments")}</h2>
        {asg?.farms.map((f) => (
          <div key={f.id} className="mb-4">
            <p className="eyebrow">{f.name}</p>
            {asg.sectors.filter((s) => s.farm_id === f.id).map((s) => {
              const st = s.releases.some((r) => r.status === "released") ? "released" : s.releases.length ? "hold" : "none";
              return (
                <div key={s.id} className="flex items-center justify-between py-2 border-b border-slate-100" data-testid={`assignment-sector-${s.code}`}>
                  <span className="font-semibold">{s.name} <span className="text-xs text-slate-500 font-mono">{s.code}</span></span>
                  <ReleaseBadge status={st} />
                </div>
              );
            })}
          </div>
        ))}
      </section>
    </div>
  );
}
